import { createElement, FC, PropsWithoutRef, ReactNode } from 'react';
import { useNewPasswordTheme } from './new-password.theme';
import {
  PasswordMessage,
  InvalidIcon,
  InvalidIconCCM,
  ValidIcon,
  ValidIconCCM,
  DefaultIcon,
  DefaultIconCCM
} from './new-password-error-message.ccm.css';

const Invalid: FC<
  Partial<InvalidIconCCM> & PropsWithoutRef<JSX.IntrinsicElements['svg']>
> = () => {
  const theme = useNewPasswordTheme();
  return (
    <InvalidIcon.svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16">
      <title>Invalid</title>
      <path
        fill={theme.negative}
        stroke={theme.negative}
        strokeWidth="2%"
        d="M9.102,6.6 L12.854625,2.847375 C13.315125,2.386875 13.315125,1.64025 12.854625,1.179375 L12.020625,0.345375 C11.560125,-0.115125 10.8135,-0.115125 10.352625,0.345375 L6.6,4.098 L2.847375,0.345375 C2.386875,-0.115125 1.64025,-0.115125 1.179375,0.345375 L0.345375,1.179375 C-0.115125,1.639875 -0.115125,2.3865 0.345375,2.847375 L4.098,6.6 L0.345375,10.352625 C-0.115125,10.813125 -0.115125,11.55975 0.345375,12.020625 L1.179375,12.854625 C1.639875,13.315125 2.386875,13.315125 2.847375,12.854625 L6.6,9.102 L10.352625,12.854625 C10.813125,13.315125 11.560125,13.315125 12.020625,12.854625 L12.854625,12.020625 C13.315125,11.560125 13.315125,10.8135 12.854625,10.352625 L9.102,6.6 Z"
      ></path>
    </InvalidIcon.svg>
  );
};

const Valid: FC<
  Partial<ValidIconCCM> & PropsWithoutRef<JSX.IntrinsicElements['svg']>
> = () => {
  const theme = useNewPasswordTheme();
  return (
    <ValidIcon.svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 16">
      <title>Valid</title>
      <path
        fill={theme.positive}
        stroke={theme.positive}
        strokeWidth="2%"
        d="M6.521175,14.04015 L0.281175,7.80015 C-0.0937125,7.4252625 -0.0937125,6.817425 0.281175,6.4425 L1.6387875,5.08485 C2.013675,4.709925 2.62155,4.709925 2.9964375,5.08485 L7.2,9.288375 L16.2035625,0.28485 C16.57845,-0.0900375 17.186325,-0.0900375 17.5612125,0.28485 L18.918825,1.6425 C19.2937125,2.0173875 19.2937125,2.625225 18.918825,3.00015 L7.878825,14.0401875 C7.5039,14.415075 6.8960625,14.415075 6.521175,14.04015 L6.521175,14.04015 Z"
      ></path>
    </ValidIcon.svg>
  );
};

const Default: FC<
  Partial<DefaultIconCCM> & PropsWithoutRef<JSX.IntrinsicElements['svg']>
> = () => {
  const theme = useNewPasswordTheme();
  return (
    <DefaultIcon.svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 22">
      <title>Default</title>
      <path
        fill={theme.neutral}
        stroke={theme.neutral}
        strokeWidth="2%"
        d="M9.3,0 C4.1625,0 0,4.1625 0,9.3 C0,14.4375 4.1625,18.6 9.3,18.6 C14.4375,18.6 18.6,14.4375 18.6,9.3 C18.6,4.1625 14.4375,0 9.3,0 Z M9.3,16.8 C5.15625,16.8 1.8,13.44375 1.8,9.3 C1.8,5.15625 5.15625,1.8 9.3,1.8 C13.44375,1.8 16.8,5.15625 16.8,9.3 C16.8,13.44375 13.44375,16.8 9.3,16.8 Z"
      ></path>
    </DefaultIcon.svg>
  );
};

export interface NewPasswordErrorMessageProps {
  values?: any;
  constraint?: string;
  children: ReactNode;
}

export const NewPasswordErrorMessage: FC<NewPasswordErrorMessageProps> = ({
  values,
  constraint,
  children
}) => {
  const theme = useNewPasswordTheme();
  const findIcon = (values: any, constraint: string) => {
    const isInvalid =
      values.error === 'required' ||
      (values.error && values.error.split(',').includes(constraint));

    if (!isInvalid) {
      return <Valid />;
    }

    if (values.touched) {
      return <Invalid />;
    }

    return <Default />;
  };
  return (
    <PasswordMessage.div $color={theme.labelColor}>
      {constraint && findIcon(values, constraint)}
      <span>{children}</span>
    </PasswordMessage.div>
  );
};
